import React, { useState, useEffect, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { hooks, PassthroughComponent } from '@adc/ui-core'
import { purchaseProfiles } from '@adc/blueprint'
import {
  gtmPreferencesNudge,
  gtmPurchaseProfileNudge,
} from '../../../shared/services/tracking'
import { PreferencesNudge, PurchaseProfileNudge } from './components'
import { useApplicationContext } from '../../auctionApplication/context'
import { parseRiddlerPreferences } from '../../../util/investorPreferences/flow.js'

export const nudgeKeys = {
  PURCHASE_PROFILE: 'purchase_profiles',
  INVESTOR_PREFERENCES: 'investor_preferences',
}
const standardDelay = 100
let delayedNudgeCheck

/**
 * NUDGE CENTER
 * - Check what nudges needs to be displayed
 * - When trying to display a nudge follow the priority order
 * - Only display one nudge at a time
 */
const NudgeContainer = ({
  elmId,
  children,
  showPreferencesTooltip,
  showPurchaseProfileTooltip,
  ...props
}) => {
  const { user } = useApplicationContext()

  let userProfileComplete
  const { onboarding_answers, purchase_profiles } = user

  if (user.logged_in) {
    userProfileComplete = {
      purchaseProfile:
        purchase_profiles &&
        purchaseProfiles.utils.checkHasACompleteProfile(purchase_profiles),
      buyerPreferences:
        onboarding_answers && parseRiddlerPreferences(onboarding_answers),
    }
  }

  const { actions, data } = hooks.useWebApplicationNudges()
  const [displayedNudge, setDisplayedNudge] = useState(null)
  const purchaseProfileTimeoutRef = useRef()
  const buyerPreferencesTimeoutRef = useRef()

  const { PURCHASE_PROFILE, INVESTOR_PREFERENCES } = nudgeKeys

  const nudgeOptions = {
    [PURCHASE_PROFILE]: {
      timeoutRef: purchaseProfileTimeoutRef,
      nudgeKey: nudgeKeys.PURCHASE_PROFILE,
      showTooltip: showPurchaseProfileTooltip,
      gtmTrack: gtmPurchaseProfileNudge,
      component: PurchaseProfileNudge,
      elmId: `${elmId}_purchase_profile`,
      isInfoComplete: userProfileComplete?.purchaseProfile,
    },
    [INVESTOR_PREFERENCES]: {
      timeoutRef: buyerPreferencesTimeoutRef,
      nudgeKey: nudgeKeys.INVESTOR_PREFERENCES,
      showTooltip: showPreferencesTooltip,
      gtmTrack: gtmPreferencesNudge,
      component: PreferencesNudge,
      elmId: `${elmId}_preferences`,
      isInfoComplete: userProfileComplete?.buyerPreferences,
    },
  }
  const openNudge = nudgeType => {
    /**
     * If you wanna know more about passing func to a setState Hook and why it was used here
     * https://github.com/facebook/react/issues/14010
     */
    setDisplayedNudge(previousDisplayedNudge => {
      const selectedNudge = nudgeOptions[nudgeType]
      if (previousDisplayedNudge || !selectedNudge?.showTooltip)
        return previousDisplayedNudge
      selectedNudge.gtmTrack()
      return nudgeType
    })
  }

  const closeNudge = nudgeType => {
    actions.nudge(nudgeOptions[nudgeType].nudgeKey)
    setDisplayedNudge(null)
  }

  const openNudgeIfNeeded = useCallback(
    nudgeType => {
      const selectedNudge = nudgeOptions[nudgeType]
      if (selectedNudge.isInfoComplete !== false) return

      const { timeoutRef, nudgeKey } = selectedNudge
      if (data.active.has(nudgeKey)) {
        return openNudge(nudgeType)
      }
      const timeRemaining = data.inactive.has(nudgeKey)
        ? actions.getRemainingTime(nudgeKey)
        : standardDelay
      timeoutRef.current = setTimeout(() => {
        actions.nudge(nudgeKey)
        openNudgeIfNeeded(nudgeType)
      }, timeRemaining)
    },
    [data, actions]
  )

  useEffect(() => {
    // We check if the nudge have to be closed
    if (
      (displayedNudge === PURCHASE_PROFILE && !showPurchaseProfileTooltip) ||
      (displayedNudge === INVESTOR_PREFERENCES && !showPreferencesTooltip)
    )
      closeNudge(displayedNudge)

    if (displayedNudge) {
      Object.entries(nudgeOptions).forEach(([nudgeType, { timeoutRef }]) => {
        // We clear the already unneeded timeouts for performance and to avoid unexpected behaviours
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
          timeoutRef.current = null
        }
      })
      return
    }
    // The request are made this way to ensure that we follow the priority order
    const nudgeList = [PURCHASE_PROFILE, INVESTOR_PREFERENCES]
    const startNudgeDisplayLogic = async () => {
      nudgeList.forEach(nudgeType => openNudgeIfNeeded(nudgeType))
    }
    // Since the useWebApplicationNudges data is read asynchronously we await for a minor period before reading it
    delayedNudgeCheck = setTimeout(
      () => startNudgeDisplayLogic(),
      standardDelay
    )
    return () => clearTimeout(delayedNudgeCheck)
  }, [
    displayedNudge,
    showPreferencesTooltip,
    showPurchaseProfileTooltip,
    openNudgeIfNeeded,
  ])

  const DisplayedComponent =
    nudgeOptions[displayedNudge]?.component || PassthroughComponent

  const sharedProps = {
    ...props,
    closeNudge,
  }

  return (
    <DisplayedComponent
      {...sharedProps}
      elmId={nudgeOptions[displayedNudge]?.elmId}
    >
      {children}
    </DisplayedComponent>
  )
}

NudgeContainer.propTypes = {
  elmId: PropTypes.string,
  children: PropTypes.node.isRequired,
  showPreferencesTooltip: PropTypes.bool,
  showPurchaseProfileTooltip: PropTypes.bool,
}

export default NudgeContainer
